import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, DatePicker, Progress, Row, Col, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { Line } from '@ant-design/plots';
import dayjs, { Dayjs } from 'dayjs';
import API_BASE_URL from '../../helpers/api/apiConfig';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface SubscriptionTabProps {
  isAdmin: boolean;
  isMaster: boolean;
  companyId?: number;
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
  onAuthMismatch: (response: Response, errorData: any) => Promise<boolean>;
}

const SubscriptionTab: React.FC<SubscriptionTabProps> = ({
  isAdmin,
  isMaster,
  companyId,
  buildAuthHeaders,
  onAuthMismatch,
}) => {
  const [usageData, setUsageData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedMetricKey, setSelectedMetricKey] = useState<string>('measurementsMonthly');
  const [historyRange, setHistoryRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(11, 'month').startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [historyGranularity, setHistoryGranularity] = useState<'month' | 'week'>('month');

  const fetchUsage = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const historyStart = historyRange[0].startOf('day').toISOString();
      const historyEnd = historyRange[1].add(1, 'day').startOf('day').toISOString();
      const response = await fetch(
        `${API_BASE_URL}/companies/${companyId}/usage?historyStart=${encodeURIComponent(historyStart)}&historyEnd=${encodeURIComponent(historyEnd)}&historyGranularity=${historyGranularity}`,
        {
          headers: {
            ...buildAuthHeaders(),
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsageData(data?.data || null);
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to load usage metrics');
      }
    } catch (error) {
      console.error('Failed to load usage metrics', error);
      message.error('Failed to load usage metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin && !isMaster) return;
    fetchUsage();
  }, [companyId, isAdmin, isMaster, historyRange, historyGranularity]);

  const metrics = useMemo(() => {
    if (!usageData?.usage || !usageData?.license) return [];
    const { usage, license } = usageData;
    const formatLimit = (limit?: number | null) => (typeof limit === 'number' ? limit : null);

    return [
      {
        label: 'Total users',
        current: usage.totalUsers,
        limit: formatLimit(license.maxTotalUsers),
        historyKey: 'usersJoined',
        historyLabel: 'Total users (cumulative)',
        isCumulative: true,
      },
      {
        label: 'Areas',
        current: usage.areas,
        limit: formatLimit(license.maxAreas),
        historyKey: 'areasCreated',
        historyLabel: 'Total areas (cumulative)',
        isCumulative: true,
      },
      {
        label: 'Monthly measurements',
        current: usage.measurementsMonthly,
        limit: formatLimit(license.maxMeasurementsMonthly),
        historyKey: 'measurementsMonthly',
        historyLabel: 'Measurements per month',
      },
      {
        label: 'Monthly AI tokens',
        current: usage.aiTokensMonthly,
        limit: formatLimit(license.maxAiTokensMonthly),
        historyKey: 'aiTokensMonthly',
        historyLabel: 'AI tokens per month',
      },
    ];
  }, [usageData]);

  const selectedMetric = metrics.find((metric) => metric.historyKey === selectedMetricKey);
  type HistorySeriesRow = {
    period: string;
    periodDate: string;
    value: number;
    series: 'Usage';
  };

  const toPeriodDate = (period: string) => {
    if (!period) return '';
    if (historyGranularity === 'week') return period;
    return period.length === 7 ? `${period}-01` : period;
  };

  const historySeries = useMemo<HistorySeriesRow[]>(() => {
    if (!usageData?.history || !selectedMetricKey) return [];
    let runningTotal = 0;
    const rows = usageData.history.map((entry: any) => {
      const value = entry[selectedMetricKey] ?? 0;
      runningTotal += value;
      const periodValue = entry.period || entry.month || entry.date;
      const periodDate = toPeriodDate(periodValue);
      return {
        period: periodValue,
        periodDate,
        value: selectedMetric?.isCumulative ? runningTotal : value,
        series: 'Usage' as const,
      };
    });
    return rows
      .filter((row: HistorySeriesRow) => !!row.periodDate && dayjs(row.periodDate).isValid())
      .sort((a: HistorySeriesRow, b: HistorySeriesRow) => {
        const diff = dayjs(a.periodDate).valueOf() - dayjs(b.periodDate).valueOf();
        if (diff !== 0) return diff;
        if (a.series === b.series) return 0;
        return a.series === 'Usage' ? -1 : 1;
      });
  }, [usageData, selectedMetricKey, selectedMetric, historyGranularity]);

  const totalInRange = useMemo(() => {
    if (!usageData?.history || !selectedMetricKey) return 0;
    if (selectedMetric?.isCumulative) {
      const last = historySeries.filter((row: HistorySeriesRow) => row.series === 'Usage').slice(-1)[0];
      return last?.value ?? 0;
    }
    return usageData.history.reduce((sum: number, entry: any) => sum + (entry[selectedMetricKey] ?? 0), 0);
  }, [usageData, selectedMetricKey, selectedMetric, historySeries]);

  const averagePerPeriod = useMemo(() => {
    if (!usageData?.history || usageData.history.length === 0) return 0;
    if (selectedMetric?.isCumulative) {
      return Math.round(totalInRange / usageData.history.length);
    }
    return Math.round(totalInRange / usageData.history.length);
  }, [usageData, totalInRange, selectedMetric]);

  const formatPeriodLabel = (value: string) => {
    if (!value) return '';
    return historyGranularity === 'week'
      ? dayjs(value).format('MMM D')
      : dayjs(value).format('MMM YYYY');
  };

  const historyMeta = useMemo(() => {
    if (!usageData?.history || usageData.history.length === 0) {
      return {
        latestLabel: '',
        totals: {},
        latestValues: {},
      };
    }

    const latestEntry = usageData.history[usageData.history.length - 1];
    const latestPeriod = latestEntry?.period || latestEntry?.month || latestEntry?.date;
    const latestLabel = historyGranularity === 'week'
      ? dayjs(toPeriodDate(latestPeriod)).format('MMM D, YYYY')
      : dayjs(toPeriodDate(latestPeriod)).format('MMM YYYY');

    const totals: Record<string, number> = {};
    const latestValues: Record<string, number> = {};

    metrics.forEach((metric) => {
      const values = usageData.history.map((entry: any) => entry[metric.historyKey] ?? 0);
      totals[metric.historyKey] = values.reduce((sum: number, value: number) => sum + value, 0);
      latestValues[metric.historyKey] = values[values.length - 1] ?? 0;
    });

    return { latestLabel, totals, latestValues };
  }, [usageData, metrics, historyGranularity]);

  const renderProgress = (current: number, limit?: number | null) => {
    if (!limit) {
      return (
        <Space direction="vertical" size={4}>
          <Tag color="blue">Unlimited</Tag>
          <Text>{current}</Text>
        </Space>
      );
    }
    const percent = Math.min(100, Math.round((current / limit) * 100));
    const status = current >= limit ? 'exception' : percent >= 80 ? 'active' : 'normal';
    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Progress percent={percent} status={status} size="small" />
        <Text>
          {current}/{limit}
        </Text>
      </Space>
    );
  };

  if (!isAdmin && !isMaster) {
    return (
      <Alert
        message="Access Restricted"
        description="Only administrators can access subscription details."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <Card title="Subscription" className="configuration-subscription-card">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text strong>Usage Status</Text>
        <Spin spinning={loading}>
          {usageData ? (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {usageData?.warnings?.length > 0 && (
                <Alert
                  message="Usage alerts"
                  description={
                    <Space direction="vertical">
                      {usageData.warnings.map((warning: any) => (
                        <Text key={warning.key}>{warning.message}</Text>
                      ))}
                    </Space>
                  }
                  type="warning"
                  showIcon
                />
              )}
              <Card size="small" className="configuration-subscription-trend-card">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space wrap>
                    <Text strong>
                      {selectedMetric?.historyLabel || 'Usage trend'}
                    </Text>
                    <RangePicker
                      className="configuration-subscription-range"
                      value={historyRange}
                      onChange={(range) => {
                        if (range && range[0] && range[1]) {
                          setHistoryRange([range[0], range[1]]);
                        }
                      }}
                    />
                    <Select
                      className="configuration-subscription-granularity"
                      value={historyGranularity}
                      onChange={(value) => setHistoryGranularity(value)}
                    >
                      <Option value="month">Monthly</Option>
                      <Option value="week">Weekly</Option>
                    </Select>
                  </Space>
                  <Space size="large">
                    <Text>
                      {selectedMetric?.isCumulative ? 'Current total' : 'Total in range'}: {totalInRange}
                    </Text>
                    <Text>
                      {selectedMetric?.isCumulative ? 'Avg added per' : 'Average per'} {historyGranularity}: {averagePerPeriod}
                    </Text>
                    {typeof selectedMetric?.limit === 'number' && (
                      <Text>Limit per month: {selectedMetric.limit}</Text>
                    )}
                  </Space>
                  <div style={{ marginTop: 4 }}>
                    {historySeries.length > 0 ? (
                      <Line
                        data={historySeries}
                        xField="periodDate"
                        yField="value"
                        seriesField="series"
                        height={240}
                        smooth
                        point={{ size: 3 }}
                        tooltip={{ showMarkers: false }}
                        xAxis={{
                          type: 'timeCat',
                          tickCount: historyGranularity === 'week' ? 8 : 6,
                          label: {
                            formatter: formatPeriodLabel,
                            autoRotate: true,
                            autoHide: true,
                          },
                        }}
                        legend={false}
                        color={() => '#1677ff'}
                        lineStyle={{ lineWidth: 2 }}
                      />
                    ) : (
                      <Text type="secondary">No usage data available for this range.</Text>
                    )}
                  </div>
                </Space>
              </Card>

              <Row gutter={[16, 16]} className="configuration-subscription-metrics-grid">
                {metrics.map((metric) => (
                  <Col key={metric.label} xs={24} md={12}>
                    <Card
                      className="configuration-subscription-metric-card"
                      size="small"
                      hoverable
                      onClick={() => setSelectedMetricKey(metric.historyKey)}
                      style={{
                        borderColor: selectedMetricKey === metric.historyKey ? '#1677ff' : undefined,
                      }}
                    >
                      <Text>{metric.label}</Text>
                      <div style={{ marginTop: 8 }}>
                        {renderProgress(metric.current, metric.limit)}
                      </div>
                      <Space direction="vertical" size={2}>
                        {historyMeta.latestLabel && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {metric.isCumulative
                              ? `Current total (as of ${historyMeta.latestLabel}): ${metric.current}`
                              : `Latest ${historyGranularity} (${historyMeta.latestLabel}): ${historyMeta.latestValues[metric.historyKey] ?? 0}`}
                          </Text>
                        )}
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {metric.isCumulative
                            ? `Added in range: ${historyMeta.totals[metric.historyKey] ?? 0}`
                            : `Total in range: ${historyMeta.totals[metric.historyKey] ?? 0}`}
                        </Text>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Space>
          ) : (
            <Text type="secondary">Usage metrics are not available.</Text>
          )}
        </Spin>
      </Space>
    </Card>
  );
};

export default SubscriptionTab;
